'use client';

import type {
  Announcements,
  DndContextProps,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  createContext,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type PointerEventHandler,
  type ReactNode,
  type WheelEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import tunnel from 'tunnel-rat';
import { Card } from '@/components/ui/card';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { toast } from "sonner";
import { cn } from '@/lib/utils';

const t = tunnel();
let hasShownHorizontalHint = false;

export type { DragEndEvent } from '@dnd-kit/core';

type KanbanItemProps = {
  id: string;
  name: string;
  column: string;
} & Record<string, unknown>;

type KanbanColumnProps = {
  id: string;
  name: string;
} & Record<string, unknown>;

type KanbanContextProps<
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps,
> = {
  columns: C[];
  data: T[];
  activeCardId: string | null;
  onColumnWheel?: WheelEventHandler<HTMLDivElement>;
};

const KanbanContext = createContext<KanbanContextProps>({
  columns: [],
  data: [],
  activeCardId: null,
});

export type KanbanBoardProps = {
  id: string;
  children: ReactNode;
  className?: string;
};

export const KanbanBoard = ({ id, children, className }: KanbanBoardProps) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  return (
    <div
      className={cn(
        'flex h-full w-[336px] min-h-40 flex-col divide-y overflow-hidden rounded-md border bg-secondary text-xs shadow-sm ring-2 transition-all',
        isOver ? 'ring-primary' : 'ring-transparent',
        className
      )}
      ref={setNodeRef}
    >
      {children}
    </div>
  );
};

export type KanbanCardProps<T extends KanbanItemProps = KanbanItemProps> = T & {
  children?: ReactNode;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  onPointerMove?: PointerEventHandler<HTMLDivElement>;
  onPointerUp?: PointerEventHandler<HTMLDivElement>;
  tabIndex?: number;
  role?: string;
  'aria-selected'?: boolean;
};

export const KanbanCard = <T extends KanbanItemProps = KanbanItemProps>({
  id,
  name,
  column: _column,
  children,
  className,
  onClick,
  onKeyDown,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  tabIndex,
  role,
  'aria-selected': ariaSelected,
}: KanbanCardProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transition,
    transform,
    isDragging,
  } = useSortable({
    id,
  });
  const { activeCardId } = useContext(KanbanContext) as KanbanContextProps;

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={cn(
          'cursor-grab gap-4 rounded-md p-3 shadow-sm',
          isDragging && 'pointer-events-none cursor-grabbing opacity-30',
          className
        )}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerMove={onPointerMove}
        tabIndex={tabIndex}
        role={role}
        aria-selected={ariaSelected}
      >
        {children ?? <p className="m-0 font-medium text-sm">{name}</p>}
      </Card>
      {activeCardId === id && (
        <t.In>
          <Card
            className={cn(
              'cursor-grab gap-4 rounded-md p-3 shadow-sm ring-2 ring-primary',
              isDragging && 'cursor-grabbing',
              className
            )}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerMove={onPointerMove}
            tabIndex={tabIndex}
            role={role}
            aria-selected={ariaSelected}
          >
            {children ?? <p className="m-0 font-medium text-sm">{name}</p>}
          </Card>
        </t.In>
      )}
    </>
  );
};

export type KanbanCardsProps<T extends KanbanItemProps = KanbanItemProps> =
  Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'id'> & {
    children: (item: T) => ReactNode;
    id: string;
  };

export const KanbanCards = <T extends KanbanItemProps = KanbanItemProps>({
  children,
  className,
  ...props
}: KanbanCardsProps<T>) => {
  const { data } = useContext(KanbanContext) as KanbanContextProps<T>;
  const { onColumnWheel } = useContext(KanbanContext) as KanbanContextProps<T>;
  const filteredData = data.filter((item) => item.column === props.id);
  const items = filteredData.map((item) => item.id);

  return (
    <ScrollArea className="relative flex-1 overflow-hidden" onWheel={onColumnWheel}>
      <SortableContext items={items}>
        <div
          className={cn('flex flex-grow flex-col gap-2 p-2', className)}
          {...props}
        >
          {filteredData.map(children)}
        </div>
      </SortableContext>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};

export type KanbanHeaderProps = HTMLAttributes<HTMLDivElement>;

export const KanbanHeader = ({ className, ...props }: KanbanHeaderProps) => (
  <div className={cn('m-0 p-2 font-semibold text-sm', className)} {...props} />
);

export type KanbanProviderProps<
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps,
> = Omit<DndContextProps, 'children'> & {
  children: (column: C) => ReactNode;
  className?: string;
  columns: C[];
  data: T[];
  onDataChange?: (data: T[]) => void;
  onDragStart?: (event: DragStartEvent) => void;
  onDragEnd?: (event: DragEndEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
};

export const KanbanProvider = <
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps,
>({
  children,
  onDragStart,
  onDragEnd,
  onDragOver,
  className,
  columns,
  data,
  onDataChange,
  ...props
}: KanbanProviderProps<T, C>) => {
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement>(null);
  const dragStartXRef = useRef<number>(0);
  const dragStartScrollLeftRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const [thumbStyle, setThumbStyle] = useState({ width: 0, left: 0 });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 12 },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const card = data.find((item) => item.id === event.active.id);
    if (card) {
      setActiveCardId(event.active.id as string);
    }
    
    onDragStart?.(event);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeItem = data.find((item) => item.id === active.id);
    const overItem = data.find((item) => item.id === over.id);

    if (!(activeItem)) {
      return;
    }

    const activeColumn = activeItem.column;
    const overColumn =
      overItem?.column ||
      columns.find(col => col.id === over.id)?.id ||
      columns[0]?.id;

    if (activeColumn !== overColumn) {
      let newData = [...data];
      const activeIndex = newData.findIndex((item) => item.id === active.id);
      const overIndex = newData.findIndex((item) => item.id === over.id);

      newData[activeIndex].column = overColumn;
      newData = arrayMove(newData, activeIndex, overIndex);

      onDataChange?.(newData);
    }

    onDragOver?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCardId(null);

    onDragEnd?.(event);

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    let newData = [...data];

    const oldIndex = newData.findIndex((item) => item.id === active.id);
    const newIndex = newData.findIndex((item) => item.id === over.id);

    newData = arrayMove(newData, oldIndex, newIndex);

    onDataChange?.(newData);
  };

  const announcements: Announcements = {
    onDragStart({ active }) {
      const { name, column } = data.find((item) => item.id === active.id) ?? {};

      return `Picked up the card "${name}" from the "${column}" column`;
    },
    onDragOver({ active, over }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};
      const newColumn = columns.find((column) => column.id === over?.id)?.name;

      return `Dragged the card "${name}" over the "${newColumn}" column`;
    },
    onDragEnd({ active, over }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};
      const newColumn = columns.find((column) => column.id === over?.id)?.name;

      return `Dropped the card "${name}" into the "${newColumn}" column`;
    },
    onDragCancel({ active }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};

      return `Cancelled dragging the card "${name}"`;
    },
  };

  const updateScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    const track = scrollbarTrackRef.current;
    if (!container || !track) return;

    const trackWidth = track.clientWidth;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const ratio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
    const thumbWidth = Math.max(trackWidth * ratio, 24);
    const maxThumb = Math.max(0, trackWidth - thumbWidth);
    const left = maxScroll > 0 ? (container.scrollLeft / maxScroll) * maxThumb : 0;

    setThumbStyle({ width: thumbWidth, left });
  }, []);

  const handleThumbDrag = useCallback((event: PointerEvent) => {
    if (!isDraggingRef.current) return;
    const container = scrollContainerRef.current;
    const track = scrollbarTrackRef.current;
    if (!container || !track) return;

    const deltaX = event.clientX - dragStartXRef.current;
    const trackWidth = track.clientWidth;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const ratio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
    const thumbWidth = Math.max(trackWidth * ratio, 24);
    const maxThumb = Math.max(0, trackWidth - thumbWidth);
    const scrollDelta = maxThumb > 0 ? (deltaX / maxThumb) * maxScroll : 0;

    container.scrollLeft = dragStartScrollLeftRef.current + scrollDelta;
  }, []);

  const stopThumbDrag = useCallback(() => {
    isDraggingRef.current = false;
    window.removeEventListener('pointermove', handleThumbDrag);
  }, [handleThumbDrag]);

  const handleThumbPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const container = scrollContainerRef.current;
      const track = scrollbarTrackRef.current;
      if (!container || !track) return;

      event.preventDefault();
      isDraggingRef.current = true;
      dragStartXRef.current = event.clientX;
      dragStartScrollLeftRef.current = container.scrollLeft;

      window.addEventListener('pointermove', handleThumbDrag);
      window.addEventListener('pointerup', stopThumbDrag, { once: true });
    },
    [handleThumbDrag, stopThumbDrag]
  );

  const handleTrackPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.target === scrollbarThumbRef.current) return;
      const container = scrollContainerRef.current;
      const track = scrollbarTrackRef.current;
      if (!container || !track) return;

      const rect = track.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const ratio = rect.width > 0 ? clickX / rect.width : 0;
      const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
      container.scrollLeft = ratio * maxScroll;
    },
    []
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    const track = scrollbarTrackRef.current;
    if (!container || !track) return;

    updateScrollbar();
    const handleScroll = () => updateScrollbar();
    container.addEventListener('scroll', handleScroll);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScrollbar());
      resizeObserver.observe(container);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', updateScrollbar);
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateScrollbar);
      }
    };
  }, [updateScrollbar]);

  const handleColumnWheel: WheelEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (hasShownHorizontalHint) return;
      if (event.deltaY === 0 || event.shiftKey) return;
      const container = scrollContainerRef.current;
      if (!container) return;
      if (container.scrollWidth <= container.clientWidth + 1) return;

      hasShownHorizontalHint = true;
      toast.info(
        "Gebruik Shift + scrollen of de scrollbalk onderaan om meer kolommen te bekijken",
        {
          duration: 3000,
        }
      );
    },
    [toast]
  );

  return (
    <KanbanContext.Provider value={{ columns, data, activeCardId, onColumnWheel: handleColumnWheel }}>
      <div className="flex h-full w-full flex-col">
        <DndContext
          accessibility={{ announcements }}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragStart={handleDragStart}
          sensors={sensors}
          {...props}
        >
          <div
            ref={scrollContainerRef}
            className="custom-scrollbar flex-1 overflow-x-auto min-h-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            <div
              className={cn(
                'grid min-h-full min-w-max auto-cols-[336px] grid-flow-col gap-4',
                className
              )}
            >
              {columns.map((column) => children(column))}
            </div>
          </div>
          <div className="px-6 pb-3 pt-1 ">
            <div
              ref={scrollbarTrackRef}
              onPointerDown={handleTrackPointerDown}
              className="relative h-2.5 w-full rounded-full bg-slate-100/70"
            >
              <div
                ref={scrollbarThumbRef}
                onPointerDown={handleThumbPointerDown}
                className="absolute top-0 h-full rounded-full bg-neutral-600/80 hover:cursor-grab active:cursor-grabbing"
                style={{ width: `${thumbStyle.width}px`, left: `${thumbStyle.left}px` }}
              />
            </div>
          </div>
          {typeof window !== 'undefined' &&
            createPortal(
              <DragOverlay>
                <t.Out />
              </DragOverlay>,
              document.body
            )}
        </DndContext>
      </div>
    </KanbanContext.Provider>
  );
};
